import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, LinearProgress, Typography, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { PRIMARY_ALPHA } from '../ColumnConfiguratorDrawer.styles';
import styles from './ColumnCounter.module.css';

interface ColumnCounterProps {
  selectedCount: number;
  effectiveMax: number;
  isAtMax: boolean;
  isLowSlots: boolean;
  slotsRemaining: number;
  progress: number;
}

export function ColumnCounter({
  selectedCount,
  effectiveMax,
  isAtMax,
  isLowSlots,
  slotsRemaining,
  progress,
}: ColumnCounterProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  return (
    <Box
      className={styles.counter}
      sx={{
        px: 2.5,
        pt: 1.75,
        pb: isAtMax || isLowSlots ? 1.25 : 1.75,
        bgcolor: alpha(primary, PRIMARY_ALPHA.sectionBg),
        borderBottom: '1px solid',
        borderColor: alpha(primary, PRIMARY_ALPHA.sectionBorder),
        flexShrink: 0,
      }}
    >
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}
      >
        <Typography
          variant="body2"
          fontWeight={theme.typography.fontWeightMedium}
          sx={{ color: 'var(--gray-600)', fontSize: 'var(--font-counter-label)' }}
        >
          {t('columnConfigurator.selected')}
        </Typography>
        <Typography
          sx={{
            fontSize: 'var(--font-counter-value)',
            fontWeight: theme.typography.fontWeightBold,
            color: isAtMax ? 'var(--error-text)' : primary,
            transition: 'color 0.2s',
          }}
        >
          {selectedCount} / {effectiveMax}
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={progress}
        color={isAtMax ? 'error' : 'primary'}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: 'var(--gray-200)',
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          },
        }}
      />

      {(isAtMax || isLowSlots) && (
        <Box
          sx={{
            mt: 1.25,
            px: 1.25,
            py: 0.875,
            borderRadius: '8px',
            bgcolor: isAtMax ? 'var(--counter-error-bg)' : 'var(--counter-warning-bg)',
            border: '1px solid',
            borderColor: isAtMax ? 'var(--counter-error-border)' : 'var(--counter-warning-border)',
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <WarningAmberIcon
            sx={{
              fontSize: 'var(--icon-action)',
              color: isAtMax ? 'var(--error)' : 'var(--warning)',
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: 'var(--font-warning)',
              color: isAtMax ? 'var(--error-text)' : 'var(--warning-text)',
              fontWeight: theme.typography.fontWeightMedium,
              lineHeight: 1.4,
            }}
          >
            {isAtMax
              ? t('columnConfigurator.maxReached', { max: effectiveMax })
              : t('columnConfigurator.lowSlots', { count: slotsRemaining })}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
